import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useCallback, useLayoutEffect, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { ColorTheme } from '@figma/fpl-themes';
import clsx from 'clsx';
import { PopupPositionCache, usePopup } from '../hooks/use-popup.js';
import { useSharedRef } from '../hooks/use-shared-ref.js';
import { getScrollablePopupScrollPosition } from '../scrollable-popup/scrollable-popup.utils.js';
import { getOwnerDocument, doRectsOverlap, getOwnerWindow } from '../utils/dom.js';
import { PREVENT_KEY_CAPTURE, PREVENT_WHEEL_CAPTURE, isPortaledEvent } from '../utils/event.js';
import { getMovementType } from '../utils/interaction.js';
import { observeResizeAll } from '../utils/observe-resize.js';
import { Arrow } from './menu-v2.arrow.js';
import { VIEWPORT_MARGIN, popupBg, scroll, scrollContent, popup, allowsIndent, MENU_ITEM_HEIGHT } from './menu-v2.css.js';

/** The distance from the focused item to the edge of the popup before we scroll */
const FOCUS_BUFFER = 2 * MENU_ITEM_HEIGHT;
/** This is an internal component used within Menu. */
const Popup = forwardRef(({ children, colorTheme, anchorRef, id, open, positioner, rareUseIndentOptOut, ...props }, ref) => {
    const popupRef = useSharedRef(ref);
    const scrollRef = useRef(null);
    const contentRef = useRef(null);
    const scrollHeight = useRef(0);
    const contentHeight = useRef(0);
    const [state, setState] = useState('grow');
    const [scrollPosition, setScrollPosition] = useState('none');
    const [hasOverlap, setHasOverlap] = useState(false);
    /** Sets state on popups that are on top of other popups so they can have bigger shadows. */
    const onPosition = useCallback((rect) => {
        // TODO: this might get really expensive if we do this for every popup at 60fps. We're not worrying about moving popups yet though!
        const allPopups = getOwnerDocument(popupRef.current ?? null).querySelectorAll('[data-fpl-popup]');
        for (const popup of allPopups) {
            if (popup === popupRef.current) {
                // Only check if it is on top of popups before itself in the document order.
                // This relies on the fact that all popups have the same z-index and stack according to
                // their document order.
                setHasOverlap(false);
                return;
            }
            const targetRect = PopupPositionCache.get(popup);
            if (targetRect && doRectsOverlap(rect, targetRect)) {
                setHasOverlap(true);
                return;
            }
        }
    }, [popupRef]);
    usePopup({
        open,
        anchorRef,
        popupRef,
        scrollRef,
        contentRef,
        positioner,
        onPosition,
        // Stay on whichever side we opened on; only flip if the current side stops fitting. Keeps a
        // menu that opened upwards for a tall loading state from snapping back down once it loads.
        sticky: true,
    });
    const update = useCallback(() => {
        setScrollPosition(getScrollablePopupScrollPosition(scrollRef.current));
    }, []);
    useLayoutEffect(() => update(), [update]);
    useLayoutEffect(() => {
        if (!scrollRef.current || !contentRef.current)
            return;
        const ownerWindow = getOwnerWindow(scrollRef.current);
        return observeResizeAll([scrollRef, contentRef], (entries) => {
            for (const entry of entries) {
                if (entry.target === scrollRef.current) {
                    scrollHeight.current = entry.borderBoxSize[0].blockSize;
                }
                else if (entry.target === contentRef.current) {
                    contentHeight.current = entry.borderBoxSize[0].blockSize;
                }
            }
            // if scroll height >= viewport height - margins && content height > scroll height
            // -> then scroll
            // -> else grow
            if (scrollHeight.current >= ownerWindow.innerHeight - VIEWPORT_MARGIN * 2 &&
                contentHeight.current > scrollHeight.current) {
                setState('scroll');
            }
            else {
                setState('grow');
            }
            flushSync(() => update());
        });
    }, [update]);
    function scrollBy(d) {
        if (!popupRef.current || !scrollRef.current) {
            return;
        }
        const height = Number.parseInt(popupRef.current.style.height);
        const ownerWindow = getOwnerWindow(popupRef.current);
        const newHeight = Math.min(contentHeight.current, ownerWindow.innerHeight - VIEWPORT_MARGIN * 2, height + Math.abs(d));
        const diff = newHeight - height;
        // already full height
        if (diff === 0)
            return 'full-height';
        if (scrollPosition === 'start') {
            // growing bottom up, only handle negative delta
            if (d < 0)
                return;
            // reset scrollTop to 0
            scrollRef.current.scrollTop = 0;
            // - increasing height
            // - decreasing top
            popupRef.current.style.height = `${newHeight}px`;
            popupRef.current.style.top = `${Number.parseInt(popupRef.current.style.top) - diff}px`;
        }
        else if (scrollPosition === 'end') {
            // growing top down, only handle positive delta
            if (d > 0)
                return;
            // reset scrollTop to max
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            // - increasing height
            // - decreasing scroll top
            popupRef.current.style.height = `${newHeight}px`;
            scrollRef.current.scrollBy(0, diff);
        }
    }
    const tick = (amount) => {
        const d = amount * MENU_ITEM_HEIGHT;
        if (scrollBy(d) === 'full-height') {
            // if we hit full height, do normal scroll
            if (scrollRef.current) {
                scrollRef.current.scrollBy(0, d);
            }
        }
    };
    const lastScrollTop = useRef(-1);
    useEffect(() => {
        lastScrollTop.current = scrollRef.current?.scrollTop ?? -1;
    }, []);
    // resize observer? what if options are added dynamically or something
    // can get ideal height from inner content wrapper
    return (jsx("div", { "data-fpl-component": "", "data-fpl-popup": "", "data-fpl-popup-overlap": hasOverlap || undefined, tabIndex: -1, ...props, ref: popupRef, className: clsx(popup, rareUseIndentOptOut !== true && allowsIndent), ...PREVENT_WHEEL_CAPTURE, ...PREVENT_KEY_CAPTURE, "data-fpl-scroll-y": scrollPosition, children: jsx(ColorTheme, { mode: "dark", ...colorTheme, children: jsxs("div", { className: popupBg, role: "none", children: [jsx("div", { className: scroll[state], role: "none", ref: scrollRef, "data-testid": "menu-v2-scroll", onScroll: () => {
                            update();
                            if (lastScrollTop.current === -1)
                                return;
                            const scrollTop = scrollRef.current.scrollTop;
                            const d = scrollTop - lastScrollTop.current;
                            lastScrollTop.current = scrollTop;
                            scrollBy(d);
                        }, onFocus: (e) => {
                            if (isPortaledEvent(e) || getMovementType() !== 'keyboard')
                                return;
                            // Ensure the focused item is always at least FOCUS_BUFFER px away from a scrolling
                            // edge. On any focus event, check the distance from either edge and scroll if needed.
                            const popupBox = popupRef.current.getBoundingClientRect();
                            const focusedBox = e.target.getBoundingClientRect();
                            const topDist = focusedBox.top - popupBox.top;
                            const bottomDist = popupBox.bottom - focusedBox.bottom;
                            // pick the smaller distance and only scroll from the closer edge
                            if (topDist < bottomDist) {
                                // closer to the top than the bottom
                                if (topDist < FOCUS_BUFFER) {
                                    scrollRef.current.scrollBy(0, topDist - FOCUS_BUFFER);
                                }
                            }
                            else {
                                // closer to the bottom than the top
                                if (bottomDist < FOCUS_BUFFER) {
                                    scrollRef.current?.scrollBy(0, FOCUS_BUFFER - bottomDist);
                                }
                            }
                        }, children: jsx("ul", { role: "menu", id: id, ref: contentRef, className: scrollContent, children: children }) }), jsx(Arrow, { dir: "up", tick: tick }), jsx(Arrow, { dir: "down", tick: tick })] }) }) }));
});
Popup.displayName = 'MenuV2.Popup';

export { Popup };
//# sourceMappingURL=menu-v2.popup.js.map
